/**
 * AppTabs - Barra de navegación principal (segunda fila)
 *
 * Contiene los tabs: Dashboard, Biblioteca, Eventos, Calendario,
 * Tareas, Nueva Tarea, Informes, Tendencias.
 *
 * Extraído de App.js líneas 6210-6298 durante la Ola A del refactor 2026-04-20.
 * Sin lógica propia — solo JSX de navegación usando los props inyectados.
 */

import { Button } from './ui/button';
import { prefetchHandlers } from '../utils/lazyPrefetch';
import {
  TrendingUp,
  Library,
  List,
  Calendar,
  CheckSquare,
  Plus,
  Sparkles,
} from 'lucide-react';

export const AppTabs = ({
  view,
  setView,
  user,
  isLiveMode,
  playlists,
  currentPlaylist,
  setCurrentPlaylist,
  navigate,
  setIsTaskFormOpen,
}) => {
  const sizeClass = isLiveMode ? 'text-base sm:text-lg px-6 py-6' : 'text-xs sm:text-sm';
  const iconSize = isLiveMode ? 'w-6 h-6' : 'w-4 h-4';
  const btnSize = isLiveMode ? 'lg' : 'sm';
  const labelVisibility = isLiveMode ? 'inline' : 'hidden sm:inline';

  return (
    <div
      className={`mb-3 sm:mb-4 flex gap-1 sm:gap-2 overflow-x-auto ${
        isLiveMode ? 'justify-center' : ''
      }`}
      data-testid="app-tabs"
    >
      <Button
        onClick={() => setView('dashboard')}
        className={`flex-shrink-0 ${sizeClass} ${
          view === 'dashboard' ? 'bg-violet-600' : 'bg-slate-700'
        }`}
        size={btnSize}
        data-testid="tab-dashboard"
      >
        <TrendingUp className={`${iconSize} sm:mr-2`} />
        <span className={labelVisibility}>
          {user?.role === 'admin' ? 'Dashboard' : 'Mis Asignaciones'}
        </span>
      </Button>

      <Button
        onClick={() => setView('library')}
        className={`flex-shrink-0 ${sizeClass} ${
          view === 'library' ? 'bg-violet-600' : 'bg-slate-700'
        }`}
        size={btnSize}
        data-testid="tab-library"
      >
        <Library className={`${iconSize} sm:mr-2`} />
        <span className={labelVisibility}>Biblioteca</span>
      </Button>

      <Button
        onClick={() => {
          if (isLiveMode && currentPlaylist) {
            setView('playlist-detail');
          } else {
            setView('playlists');
            setCurrentPlaylist(null);
          }
        }}
        className={`flex-shrink-0 ${sizeClass} ${
          view === 'playlists' || view === 'playlist-detail'
            ? 'bg-violet-600'
            : 'bg-slate-700'
        }`}
        size={btnSize}
        data-testid="tab-playlists"
      >
        <List className={`${iconSize} sm:mr-2`} />
        <span className={labelVisibility}>
          {isLiveMode ? 'Evento' : `Eventos (${playlists.length})`}
        </span>
        {!isLiveMode && (
          <span className="sm:hidden">({playlists.length})</span>
        )}
      </Button>

      {!isLiveMode && (
        <>
          <Button
            onClick={() => navigate('/calendar')}
            {...prefetchHandlers('/calendar')}
            className={`flex-shrink-0 text-xs sm:text-sm ${
              view === 'calendar' ? 'bg-violet-600' : 'bg-slate-700'
            }`}
            size="sm"
            data-testid="tab-calendar"
          >
            <Calendar className="w-4 h-4 sm:mr-2" />
            <span className="hidden sm:inline">Calendario</span>
          </Button>

          {user?.role === 'admin' && (
            <Button
              onClick={() => navigate('/tasks')}
              {...prefetchHandlers('/tasks')}
              className={`flex-shrink-0 text-xs sm:text-sm ${
                view === 'tasks' ? 'bg-violet-600' : 'bg-slate-700'
              }`}
              size="sm"
              data-testid="tab-tasks"
            >
              <CheckSquare className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Tareas</span>
            </Button>
          )}

          {user?.role === 'admin' && (
            <Button
              onClick={() => setIsTaskFormOpen(true)}
              className="flex-shrink-0 text-xs sm:text-sm bg-violet-600 hover:bg-violet-700"
              size="sm"
              title="Nueva Tarea"
              data-testid="tab-new-task"
            >
              <Plus className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Nueva Tarea</span>
            </Button>
          )}

          {user?.role === 'admin' && (
            <Button
              onClick={() => navigate('/reports')}
              {...prefetchHandlers('/reports')}
              className={`flex-shrink-0 text-xs sm:text-sm ${
                view === 'reports' ? 'bg-violet-600' : 'bg-slate-700'
              }`}
              size="sm"
              data-testid="tab-reports"
            >
              <TrendingUp className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Informes</span>
            </Button>
          )}
        </>
      )}

      <Button
        onClick={() => navigate('/trends')}
        {...prefetchHandlers('/trends')}
        className={`flex-shrink-0 ${sizeClass} ${
          view === 'tendencias' ? 'bg-violet-600' : 'bg-slate-700'
        }`}
        size={btnSize}
        data-testid="tab-trends"
      >
        <Sparkles className={`${iconSize} sm:mr-2`} />
        <span className={labelVisibility}>Tendencias</span>
      </Button>
    </div>
  );
};

export default AppTabs;
